import dayjs from 'dayjs';
import { PositionModel, OptionType, PositionType, Side } from '../../../../../../Generated/Commodity-Management-Api';

/**
 * Known custom data fields stored as JSON in the Position.CustomData field.
 * No index signature so this interface can safely be intersected with other
 * strongly-typed models (e.g. PositionModel).
 */
export interface KnownPositionCustomData {
    tp?: number;           // Time Premium
    decile?: number;       // Decile ranking (1-10)
    fees?: number;         // Transaction fees
    fee?: number;          // Transaction fee (singular alias)
    currentAsset?: number;
    currentLiability?: number;
    currentAssetGainLoss?: number;
    currentLiabilityGainLoss?: number;
    optionSettlement?: number;
}

/**
 * Custom data stored as JSON in the Position.CustomData field.
 * Includes an index signature for additional unknown custom fields.
 */
export interface PositionCustomData extends KnownPositionCustomData {
    [key: string]: number | string | boolean | undefined; // Allow additional custom fields
}

/**
 * Parse CustomData JSON string from position.
 * Keys are normalized to lowercase so callers don't have to worry about casing
 * (legacy systems may store "Fees" or "fees").
 * Numeric string values are coerced to numbers for convenience.
 */
export const parseCustomData = (customData: string | null | undefined): PositionCustomData => {
    if (!customData) return {};
    const normalizeKeys = (obj: Record<string, unknown>): PositionCustomData => {
        const normalized: PositionCustomData = {};
        Object.keys(obj).forEach(key => {
            const value = obj[key];
            const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
            if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) {
                normalized[lowerKey] = Number(value);
            } else if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
                normalized[lowerKey] = value;
            }
        });
        return normalized;
    };
    try {
        const parsed = JSON.parse(customData);
        if (parsed && typeof parsed === 'object') {
            return normalizeKeys(parsed);
        }
    } catch {
        // Fallback: tolerate malformed pseudo-JSON like `{"fees";6.35}` where
        // semicolons were used instead of colons, or unquoted keys/values.
        const cleaned = customData
            .replace(/[{}]/g, '')
            .trim();
        if (cleaned) {
            const result: Record<string, unknown> = {};
            cleaned.split(',').forEach(pair => {
                const sepIdx = pair.search(/[;:]/);
                if (sepIdx < 0) return;
                const rawKey = pair.substring(0, sepIdx).trim().replace(/^["']|["']$/g, '');
                const rawVal = pair.substring(sepIdx + 1).trim().replace(/^["']|["']$/g, '');
                if (rawKey) result[rawKey] = rawVal;
            });
            return normalizeKeys(result);
        }
    }
    return {};
};

/**
 * Extract fees from a position's customData, accepting either "fees" or "fee" as the key.
 */
export const getFees = (position: PositionModel): number => {
    const customData = parseCustomData(position.customData);
    return customData.fees ?? customData.fee ?? 0;
};

/**
 * Extended position model with parsed custom data
 */
export interface ExtendedPositionModel extends PositionModel {
    customDataParsed?: PositionCustomData;
    // Computed fields for reports
    positionValue?: number;
    marketValue?: number;
    tool?: string;
}

/**
 * Get tool description from position type and option type
 */
export const getToolDescription = (position: PositionModel): string => {
    if (position.positionType === PositionType.Future) {
        return 'Future';
    }
    if (position.positionType === PositionType.Physical) {
        return 'Physical';
    }
    if (position.positionType === PositionType.Option) {
        if (position.side === Side.Sell && position.optionType === OptionType.Call) {
            return 'Call (Sold)';
        }
        return position.optionType?.toString() || 'Option';
    }
    return position.positionType?.toString() || 'Unknown';
};

/**
 * Calculate position value (price * volume, adjusted for side)
 */
export const calculatePositionValue = (position: PositionModel): number => {
    const price = position.price ?? 0;
    const volume = position.volume ?? 0;
    const sideMultiplier = position.side === Side.Buy ? 1 : -1;
    return price * volume * sideMultiplier;
};

/**
 * Calculate market value (market price * volume, adjusted for side)
 */
export const calculateMarketValue = (position: PositionModel): number => {
    const marketPrice = position.markToMarketPrice ?? 0;
    const volume = position.volume ?? 0;
    const sideMultiplier = position.side === Side.Buy ? 1 : -1;
    return marketPrice * volume * sideMultiplier;
};

/**
 * Calculate MTM value (market value - position value)
 */
export const calculateMtmValue = (position: PositionModel): number => {
    return calculateMarketValue(position) - calculatePositionValue(position);
};

/**
 * Calculate purchase cost/proceeds (price * volume, negative for buys)
 */
export const calculatePurchaseCost = (position: PositionModel): number => {
    const price = position.price ?? 0;
    const volume = position.volume ?? 0;
    // For buys, this is a cost (negative), for sells it's proceeds (positive)
    const sideMultiplier = position.side === Side.Buy ? -1 : 1;
    return price * volume * sideMultiplier;
};

/**
 * Calculate realized value from settlement/expiration price.
 * Only futures that have been liquidated have a realized value; options and
 * futures not yet expired return 0.
 *
 * `asOfDate` is the report's marking-to-market date, not today - a report run
 * as of a past date must judge liquidation against that date, otherwise
 * positions that expired after it would be counted as already realized.
 */
export const calculateRealizedValue = (position: PositionModel, asOfDate: Date): number => {
    if (position.positionType !== PositionType.Future) return 0;
    if (!position.expirationDate) return 0;
    // dayjs handles both Date and Dayjs inputs, which matters because
    // StoneXDatePicker hands back a Dayjs through a `Date`-typed prop.
    const isLiquidated = !dayjs(position.expirationDate).isAfter(dayjs(asOfDate));
    if (!isLiquidated) return 0;

    const settlementPrice = position.markToMarketPrice ?? 0;
    const volume = position.volume ?? 0;
    const sideMultiplier = position.side === Side.Buy ? 1 : -1;
    return settlementPrice * volume * sideMultiplier;
};

/**
 * Calculate net value gain/loss for closed positions
 */
export const calculateNetValueGainLoss = (position: PositionModel, asOfDate: Date): number => {
    return calculateRealizedValue(position, asOfDate) + calculatePurchaseCost(position);
};

/**
 * Calculate closed report summary
 */
export interface ClosedReportSummary {
    totalQuantity: number;
    totalPurchaseCost: number;
    totalRealizedValue: number;
    totalNetValueGainLoss: number;
    totalFees: number;
}

export const calculateClosedReportSummary = (positions: PositionModel[], asOfDate: Date): ClosedReportSummary => {
    return positions.reduce((summary, position) => {
        // MMBtus total: only sum Buy-side positions (ignore sells)
        const quantity = position.side === Side.Buy ? (position.volume ?? 0) : 0;
        const purchaseCost = calculatePurchaseCost(position);
        const realizedValue = calculateRealizedValue(position, asOfDate);
        const netValueGainLoss = calculateNetValueGainLoss(position, asOfDate);
        const customData = parseCustomData(position.customData);
        const fees = customData.fees ?? 0;

        return {
            totalQuantity: summary.totalQuantity + quantity,
            totalPurchaseCost: summary.totalPurchaseCost + purchaseCost,
            totalRealizedValue: summary.totalRealizedValue + realizedValue,
            totalNetValueGainLoss: summary.totalNetValueGainLoss + netValueGainLoss,
            totalFees: summary.totalFees + fees,
        };
    }, {
        totalQuantity: 0,
        totalPurchaseCost: 0,
        totalRealizedValue: 0,
        totalNetValueGainLoss: 0,
        totalFees: 0,
    });
};

/**
 * Calculate current asset (for Call options)
 */
export const calculateCurrentAsset = (position: PositionModel): number => {
    if (position.positionType === PositionType.Option && position.optionType === OptionType.Call) {
        return calculatePositionValue(position);
    }
    return 0;
};

/**
 * Calculate current liability (for non-Call positions)
 */
export const calculateCurrentLiability = (position: PositionModel): number => {
    if (position.positionType !== PositionType.Option || position.optionType !== OptionType.Call) {
        return calculatePositionValue(position);
    }
    return 0;
};

/**
 * Calculate current asset gain/loss (for Call options)
 */
export const calculateCurrentAssetGainLoss = (position: PositionModel): number => {
    if (position.positionType === PositionType.Option && position.optionType === OptionType.Call) {
        return calculateMtmValue(position);
    }
    return 0;
};

/**
 * Calculate current liability gain/loss (for non-Call positions)
 */
export const calculateCurrentLiabilityGainLoss = (position: PositionModel): number => {
    if (position.positionType !== PositionType.Option || position.optionType !== OptionType.Call) {
        return calculateMtmValue(position);
    }
    return 0;
};

/**
 * Calculate weighted average price
 * Formula: sum(price * volume) / sum(volume)
 */
export const calculateWeightedAverage = (positions: PositionModel[], priceField: 'price' | 'optionStrike' = 'price'): number => {
    const totalVolume = positions.reduce((sum, p) => sum + (p.volume ?? 0), 0);
    if (totalVolume === 0) return 0;

    const weightedSum = positions.reduce((sum, p) => {
        const price = priceField === 'price' ? (p.price ?? 0) : (p.optionStrike ?? 0);
        return sum + price * (p.volume ?? 0);
    }, 0);

    return weightedSum / totalVolume;
};

/**
 * Group positions by tenure date (budget date)
 */
export const groupByTenureDate = (positions: PositionModel[]): Map<string, PositionModel[]> => {
    const groups = new Map<string, PositionModel[]>();

    positions.forEach(position => {
        const key = `${position.budgetDate.year}-${position.budgetDate.month}`;
        const existing = groups.get(key) || [];
        existing.push(position);
        groups.set(key, existing);
    });

    return groups;
};

/**
 * Group positions by contract date
 */
export const groupByContractDate = (positions: PositionModel[]): Map<string, PositionModel[]> => {
    const groups = new Map<string, PositionModel[]>();

    positions.forEach(position => {
        if (position.contractDate) {
            const key = `${position.contractDate.year}-${position.contractDate.month}`;
            const existing = groups.get(key) || [];
            existing.push(position);
            groups.set(key, existing);
        }
    });

    return groups;
};

/**
 * Filter positions by expiry status
 */
export const filterByExpiry = (positions: PositionModel[], quoteDate: Date, includeExpired: boolean): PositionModel[] => {
    return positions.filter(p => {
        if (!p.expirationDate) return true;
        const isExpired = new Date(p.expirationDate) < quoteDate;
        return includeExpired ? isExpired : !isExpired;
    });
};

/**
 * MTM Report row structure
 */
export interface MtmReportRow {
    tenureDate: string;
    formattedDate: string;
    tool: string;
    counterpartyName: string;
    tradeDate: string;
    quantity: number;
    strike: number;
    price: number;
    purchaseCost: number;
    positionValue: number;
    marketPrice: number;
    marketValue: number;
    mtmValue: number;
    netVsMarket: number;
    commodity: string;
    dealTicketId: string;
    fees: number;
    expirationDate?: string;
    position: PositionModel;
}

/**
 * MTM Closed Report row structure
 * Columns: Period, Tool, Counterparty, Original Trade Date, MMBtus, Strike/Fixed Price, 
 *          Purchase Price, Purchase Cost/Proceeds, Trade/Expiration Date, Expiration Price, 
 *          Realized Value, Net Value Realized Gain or (Loss), Fees
 */
export interface MtmClosedReportRow {
    tenureDate: string;
    formattedDate: string;
    tool: string;
    counterpartyName: string;
    tradeDate: string;
    quantity: number;           // MMBtus Purchased Per Month
    strike: number;             // Strike/Fixed Price
    purchasePrice: number;      // Purchase Price
    purchaseCost: number;       // Purchase Cost/Proceeds
    expirationDate: string;     // Trade / Expiration Date
    expirationPrice: number;    // Expiration Price (settlement price)
    realizedValue: number;      // Realized Value
    netValueGainLoss: number;   // Net Value Realized Gain or (Loss)
    fees: number;
    position: PositionModel;
}

/**
 * Position Report row structure
 */
export interface PositionReportLegacyRow {
    tenureDate: string;
    formattedDate: string;
    contracts: number;
    type: string;
    tool: string;
    price: number;
    strike: number;
    coverage: number;
    cumulativeCoverage: number;
    tradeDate: string;
    maxContracts: number;
    commodity: string;
    dealTicketId: string;
    positionValue: number;
    strikePrice: number;
    marketPrice: number;
    tp?: number;
    decile?: string;
    counterpartyName: string;
    position: PositionModel;
}

/**
 * Gross Hedging Report row structure
 */
export interface GrossHedgingReportRow {
    tenureDate: string;
    formattedDate: string;
    tool: string;
    counterpartyName: string;
    tradeDate: string;
    quantity: number;
    strike: number;
    price: number;
    positionValue: number;
    mtmValue: number;
    commodity: string;
    dealTicketId: string;
    currentAsset: number;
    currentLiability: number;
    currentAssetGainLoss: number;
    currentLiabilityGainLoss: number;
    position: PositionModel;
}

/**
 * Weighted Cost Average Report row structure
 */
export interface WeightedCostAvgReportRow {
    tenureDate: string;
    counterpartyName: string;
    tradeDate: string;
    contracts: number;
    tool: string;
    strike: number;
    purchasePrice: number;
    totalContractsPurchased: number;
    maxContracts: number;
    weightedStrikeAverage: number;
    weightedPurchasePriceAverage: number;
    position: PositionModel;
}

/**
 * Combined Report row structure
 */
export interface CombinedReportRow {
    tenureDate: string;
    formattedDate: string;
    tool: string;
    counterpartyName: string;
    tradeDate: string;
    quantity: number;
    strike: number;
    price: number;
    positionValue: number;
    marketPrice: number;
    marketValue: number;
    mtmValue: number;
    commodity: string;
    dealTicketId: string;
    fees: number;
    tp?: number;
    decile?: string;
    position: PositionModel;
}

/**
 * Open Contracts Report row structure
 */
export interface OpenContractsReportRow {
    tenureDate: string;
    month: string;
    totalContracts: number;
    buyCallContracts: number;
    sellCallContracts: number;
    buyFutureContracts: number;
}

/**
 * Report summary totals
 */
export interface ReportSummary {
    totalQuantity: number;
    totalPositionValue: number;
    totalMarketValue: number;
    totalMtmValue: number;
    totalFees: number;
    totalCurrentAsset?: number;
    totalCurrentLiability?: number;
    totalCurrentAssetGainLoss?: number;
    totalCurrentLiabilityGainLoss?: number;
}

/**
 * Calculate report summary from positions
 */
export const calculateReportSummary = (positions: PositionModel[]): ReportSummary => {
    return positions.reduce((summary, position) => {
        const quantity = position.volume ?? 0;
        const positionValue = calculatePositionValue(position);
        const marketValue = calculateMarketValue(position);
        const mtmValue = calculateMtmValue(position);
        const customData = parseCustomData(position.customData);
        const fees = customData.fees ?? 0;

        return {
            totalQuantity: summary.totalQuantity + quantity,
            totalPositionValue: summary.totalPositionValue + positionValue,
            totalMarketValue: summary.totalMarketValue + marketValue,
            totalMtmValue: summary.totalMtmValue + mtmValue,
            totalFees: summary.totalFees + fees,
            totalCurrentAsset: (summary.totalCurrentAsset ?? 0) + calculateCurrentAsset(position),
            totalCurrentLiability: (summary.totalCurrentLiability ?? 0) + calculateCurrentLiability(position),
            totalCurrentAssetGainLoss: (summary.totalCurrentAssetGainLoss ?? 0) + calculateCurrentAssetGainLoss(position),
            totalCurrentLiabilityGainLoss: (summary.totalCurrentLiabilityGainLoss ?? 0) + calculateCurrentLiabilityGainLoss(position),
        };
    }, {
        totalQuantity: 0,
        totalPositionValue: 0,
        totalMarketValue: 0,
        totalMtmValue: 0,
        totalFees: 0,
        totalCurrentAsset: 0,
        totalCurrentLiability: 0,
        totalCurrentAssetGainLoss: 0,
        totalCurrentLiabilityGainLoss: 0,
    });
};
